import React, { useEffect, useState } from 'react';
import { useGetFiltersQuery } from '../../../redux/slices/homeSlice';
import { useFormik } from 'formik';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import Accordion from '../ShoppingBag/Accordion';
import "./Category.scss";
import { CLOSE_BLACK_IC } from '../../../assets';

export const FilterSidePanel = ({
  showFilter,
  showFilterHandler,
  category,
  setSortBy,
  totalDocs,
  setAttributeFilters,
  filterAttributesForShoppingBag,
  setStockValue,
}) => {
  const params = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const sortby = [
    { name: 'Newest Arrivals', value: 'date-added-desc' },
    { name: 'Net Wt.- High to Low', value: 'high' },
    { name: 'Net Wt.- Low to High', value: 'low' },
  ];

  // Read initial sortby from URL if exists
  const initialSortValue = searchParams.get('netWeight');
  const [selectedSortby, setSelectedSortby] = useState(
    sortby.find((item) => item.value === initialSortValue) || sortby[0]
  );

  useEffect(() => {
    setSortBy(selectedSortby);
    if (selectedSortby) {
      searchParams.set('netWeight', selectedSortby.value);
      setSearchParams(searchParams);
    }
  }, [selectedSortby]);

  const { data: filtersData } = useGetFiltersQuery({
    queryParams: category
      ? `category=${category}`
      : `${params?.searchProductParams ? `title=${params?.searchProductParams}` : ""}`,
  }, {
    skip: location.pathname === '/shopping-bag'
  });

  // Parse query params into initialValues
  const getInitialFilterValues = () => {
    const initialValues = {};
    for (const [key, value] of searchParams.entries()) {
      if (key !== 'netWeight' && key !== 'stock') {
        initialValues[key] = value.split(',');
      }
    }
    return initialValues;
  };

  const filterFormik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialFilterValues(),
    onSubmit: () => {},
    validate: () => {
      return {};
    },
  });

  const updateURLParamsFromFilters = (values) => {
    const newParams = new URLSearchParams(searchParams);
    Object.keys(values).forEach((key) => {
      if (values[key].length > 0) {
        newParams.set(key, values[key].join(','));
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Update filters and URL on filter change
  useEffect(() => {
    let filters = {};
    Object.keys(filterFormik.values).forEach((key) => {
      if (filterFormik.values[key].length > 0) {
        filters[key] = filterFormik.values[key].join(',');
      }
    });
    setAttributeFilters(filters);
    updateURLParamsFromFilters(filterFormik.values);
  }, [filterFormik.values]);

  const [dataToMap, setDataToMap] = useState([]);
  useEffect(() => {
    if (location.pathname === '/shopping-bag') {
      setDataToMap(filterAttributesForShoppingBag);
    } else {
      setDataToMap(filtersData);
    }
  }, [location.pathname, filtersData, filterAttributesForShoppingBag]);

  const initialStock = searchParams.get('stock');
  const [checkboxState, setCheckboxState] = useState({
    inStock: initialStock === 'inStock' || initialStock === 'all',
    makeToOrder: initialStock === 'makeToOrder' || initialStock === 'all',
  });

  const handleChange = (e) => {
    const { value, checked } = e.target;
    setCheckboxState((prevState) => ({
      ...prevState,
      [value]: checked,
    }));
  };

  useEffect(() => {
    const { inStock, makeToOrder } = checkboxState;
    let stockParam = "all";

    if (inStock && makeToOrder) {
      stockParam = "all";
      setStockValue("all");
    } else if (inStock) {
      stockParam = "inStock";
      setStockValue("inStock");
    } else if (makeToOrder) {
      stockParam = "makeToOrder";
      setStockValue("makeToOrder");
    } else {
      stockParam = "all";
      setStockValue("all");
    }

    searchParams.set('stock', stockParam);
    setSearchParams(searchParams);
  }, [checkboxState]);

  return (
    <div className={`filter-side-panel ${showFilter ? 'show' : ''}`}>
      <div className="overlay" onClick={showFilterHandler}></div>
      <div className="panel bg-text_light_2">
        <div className="filter-body">
          <div className="sortby">
            <div className='w-full flex justify-end'>
              <img src={CLOSE_BLACK_IC} alt="" className='cursor-pointer' onClick={showFilterHandler} />
            </div>
            <div className="title pb-2 text-text_dark_2 cormorant-bold">Sort By</div>
            {sortby.map((item, index) => (
              <div
                key={index}
                className={
                  item.name === selectedSortby.name
                    ? "sortby-item sortby-item-active bg-primary text-text_light_2 lato-regular border-[1px] border-primary"
                    : "sortby-item border-[1px] border-primary text-primary lato-regular"
                }
                onClick={() => setSelectedSortby(item)}
              >
                {item.name}
              </div>
            ))}
          </div>

          <div className="filterby">
            <div className="d-flex j-sb">
              <div className="title text-text_dark_2 cormorant-bold">Filter By</div>
              <div
                className="clear-all text-text_dark_2 cormorant-bold"
                onClick={() => {
                  setSearchParams({});
                  filterFormik.resetForm();
                  setStockValue("all");
                  setCheckboxState({inStock: false, makeToOrder:false});
                  setTimeout(filterFormik.handleSubmit(), 0);
                }}
              >
                Clear all
              </div>
            </div>
            <div className="attribute-filters">
              {dataToMap?.attribute_filters?.map((item, index) => (
                <Accordion
                  key={index}
                  title={<div className="title cormorant-bold">{item.attributeName}</div>}
                  content={
                    <div className="filter-items">
                      {item.attributeValues.map((option, index) => (
                        <div
                          className="check-container d-flex"
                          key={`${index}-${option}`}
                        >
                          <input
                            type="checkbox"
                            id={`${item.attributeName}-${option}`}
                            name={item.attributeName}
                            value={option}
                            onChange={(e) => {
                              filterFormik.handleChange(e);
                              setTimeout(filterFormik.handleSubmit(), 0);
                            }}
                            checked={filterFormik.values[item.attributeName]?.includes(option) || false}
                            className="accent-black"
                          />
                          <label
                            className="ps-3 flex-grow cursor-pointer align-middle lato-regular"
                            htmlFor={`${item.attributeName}-${option}`}
                          >
                            {option}
                          </label>
                        </div>
                      ))}
                    </div>
                  }
                />
              ))}

              <Accordion
                title={<div className="title cormorant-bold">Stock</div>}
                content={
                  <div className="static-checks">
                    <div className="check-container d-flex">
                      <input
                        type="checkbox"
                        id="Stock-In stock"
                        name="Stock"
                        value="inStock"
                        className="accent-black"
                        checked={checkboxState.inStock}
                        onChange={handleChange}
                      />
                      <label
                        className="ps-3 flex-grow cursor-pointer align-middle lato-regular"
                        htmlFor="Stock-In stock"
                      >
                        In stock
                      </label>
                    </div>
                    <div className="check-container d-flex">
                      <input
                        type="checkbox"
                        id="Stock-Make to order"
                        name="Stock"
                        value="makeToOrder"
                        className="accent-black"
                        checked={checkboxState.makeToOrder}
                        onChange={handleChange}
                      />
                      <label
                        className="ps-3 flex-grow cursor-pointer align-middle lato-regular"
                        htmlFor="Stock-Make to order"
                      >
                        Make to order
                      </label>
                    </div>
                  </div>
                }
              />
            </div>
          </div>
        </div>

        <div
          className="results-btn border-[1px] border-primary text-primary lato-regular"
          onClick={() => {
            filterFormik.handleSubmit();
            showFilterHandler();
          }}
        >
          {totalDocs} RESULTS FOR YOUR SEARCH
        </div>
      </div>
    </div>
  );
};


// import React, { useEffect, useState } from 'react';
// import { useGetFiltersQuery } from '../../../redux/slices/homeSlice';
// import { useFormik } from 'formik';
// import { useLocation, useParams } from 'react-router-dom';
// import Accordion from '../ShoppingBag/Accordion';
// import "./Category.scss";
// import { CLOSE_BLACK_IC } from '../../../assets';
// export const FilterSidePanel = ({
//   showFilter,
//   showFilterHandler,
//   category,
//   setSortBy,
//   totalDocs,
//   setAttributeFilters,
//   filterAttributesForShoppingBag,
//   setStockValue,
// }) => {
//   const params = useParams();
//   const sortby = [
//     { name: 'Newest Arrivals', value: 'date-added-desc' },
//     { name: 'Net Wt.- High to Low', value: 'high' },
//     { name: 'Net Wt.- Low to High', value: 'low' },
//   ];
//   const [selectedSortby, setSelectedSortby] = useState({});
//   useEffect(() => {
//     setSelectedSortby(sortby[0]);
//   }, []);
//   useEffect(() => {
//     setSortBy(selectedSortby);
//   }, [selectedSortby]);
//   const location = useLocation();
//   const { data: filtersData, isLoading, isFetching, isSuccess, isError, error } = useGetFiltersQuery({
//     queryParams: category
//       ? `category=${category}`
//       : `${params?.searchProductParams
//         ? `title=${params?.searchProductParams}`
//         : ""
//       }`,
//   },
//     { skip: location.pathname === '/shopping-bag' ? true : false }
//   );

//   const filterFormik = useFormik({
//     enableReinitialize: true,
//     initialValues: {},
//     onSubmit: (values) => {
//       // console.log(values);
//     },
//     validate: (values) => {
//       let errors = {};
//       return errors;
//     },
//   });

//   const setAsMyFilter = () => {
//     console.log(filterFormik.values);
//   }

//   // json data for filters using filterFormik.values making key value pairs
//   useEffect(() => {
//     let filters = {};
//     Object.keys(filterFormik.values).forEach((key) => {
//       if (filterFormik.values[key].length > 0) {
//         filters[key] = filterFormik.values[key].join(',');
//       }
//     });
//     // console.log(filters);
//     setAttributeFilters(filters);
//   }, [filterFormik.values]);

//   const [dataToMap, setDataToMap] = useState([]);
//   useEffect(() => {
//     if (location.pathname === '/shopping-bag') {
//       setDataToMap(filterAttributesForShoppingBag);
//     } else {
//       setDataToMap(filtersData);
//     }
//   }, [location.pathname, filtersData, filterAttributesForShoppingBag]);
//   const [checkboxState, setCheckboxState] = useState({
//     inStock: false,
//     makeToOrder: false,
//   });

//   const handleChange = (e) => {
//     const { value, checked } = e.target;
//     setCheckboxState((prevState) => ({
//       ...prevState,
//       [value]: checked,
//     }));
//   };

//   useEffect(() => {
//     const { inStock, makeToOrder } = checkboxState;
//     if (inStock && makeToOrder) {
//       setStockValue("all");
//       console.log("Both are checked");
//     } else if (inStock) {
//       setStockValue("inStock");
//       console.log("In stock is checked");
//     } else if (makeToOrder) {
//       setStockValue("makeToOrder");
//       console.log("Make to order is checked");
//     } else {
//       setStockValue("all");
//       console.log("None are checked");
//     }
//   }, [checkboxState]);
//   return (
//     <div className={`filter-side-panel ${showFilter ? 'show' : ''}`}>
//       <div className="overlay" onClick={showFilterHandler}></div>
//       <div className="panel bg-text_light_2">
//         <div className="filter-body">
//           <div className="sortby">
//           <div className='w-full flex justify-end'>
//             <img src={CLOSE_BLACK_IC} alt="" className='cursor-pointer' onClick={showFilterHandler} />
//           </div>
//             <div className="title pb-2 text-text_dark_2 cormorant-bold">Sort By</div>
//             {sortby?.map((item, index) => (
//               <div
//                 key={index}
//                 className={
//                   item.name === selectedSortby.name
//                     ? "sortby-item sortby-item-active bg-primary text-text_light_2 lato-regular border-[1px] border-primary"
//                     : "sortby-item border-[1px] border-primary text-primary lato-regular"
//                 }
//                 onClick={() => setSelectedSortby(item)}
//               >
//                 {item.name}
//               </div>
//             ))}
//           </div>
//           <div className="filterby">
//             <div className="d-flex j-sb">
//               <div className="title text-text_dark_2 cormorant-bold">Filter By</div>
//               <div
//                 className="clear-all text-text_dark_2 cormorant-bold"
//                 onClick={() => {
//                   filterFormik.resetForm({
//                     values: filterFormik.initialValues,
//                   });
//                   setTimeout(filterFormik.handleSubmit(), 0);
//                 }}
//               >
//                 Clear all
//               </div>
//             </div>
//             {/* <div className="set-my-filter" onClick={setAsMyFilter}>Set as my filter</div> */}
//             <div className="attribute-filters">
//               {dataToMap?.attribute_filters?.map((item, index) => (
//                 <Accordion
//                   key={index}
//                   title={<div className="title cormorant-bold">{item.attributeName}</div>}
//                   content={
//                     <>
//                       <div className="filter-items">
//                         {item?.attributeValues.map((option, index) => {
//                           return (
//                             <div
//                               className="check-container d-flex"
//                               key={`${index}-${option}`}
//                             >
//                               <input
//                                 type="checkbox"
//                                 id={`${item.attributeName}-${option}`}
//                                 name={item.attributeName}
//                                 onChange={(e) => {
//                                   filterFormik.handleChange(e);
//                                   setTimeout(filterFormik.handleSubmit(), 0);
//                                 }}
//                                 value={option}
//                                 onBlur={filterFormik.handleBlur}
//                                 checked={filterFormik.values[item.attributeName]?.includes(option) || false}
//                                 className='accent-black'
//                               />
//                               <label
//                                 className='ps-3 flex-grow cursor-pointer align-middle lato-regular'
//                                 htmlFor={`${item.attributeName}-${option}`}>{option}</label><br></br>
//                             </div>
//                           );
//                         })}
//                       </div>
//                     </>
//                   }
//                 />
//               ))}
//               <Accordion
//                 title={<div className="title cormorant-bold">Stock</div>}
//                 content={
//                   <div className="static-checks">
//                     <div className="check-container d-flex">
//                       <input
//                         type="checkbox"
//                         id="Stock-In stock"
//                         name="Stock"
//                         value="inStock"
//                         className="accent-black"
//                         checked={checkboxState.inStock}
//                         onChange={handleChange}
//                       />
//                       <label
//                         className="ps-3 flex-grow cursor-pointer align-middle lato-regular"
//                         htmlFor="Stock-In stock"
//                       >
//                         In stock
//                       </label>
//                     </div>
//                     <div className="check-container d-flex">
//                       <input
//                         type="checkbox"
//                         id="Stock-Make to order"
//                         name="Stock"
//                         value="makeToOrder"
//                         className="accent-black"
//                         checked={checkboxState.makeToOrder}
//                         onChange={handleChange}
//                       />
//                       <label
//                         className="ps-3 flex-grow cursor-pointer align-middle lato-regular"
//                         htmlFor="Stock-Make to order"
//                       >
//                         Make to order
//                       </label>
//                     </div>
//                   </div>
//                 }
//               />
//             </div>
//           </div>
//         </div>
//         <div
//           className="results-btn border-[1px] border-primary text-primary lato-regular"
//           onClick={() => {
//             filterFormik.handleSubmit();
//             showFilterHandler();
//           }}
//         >
//           {totalDocs} RESULTS FOR YOUR SEARCH
//         </div>
//       </div>
//     </div>
//   );
// };